import React from 'react';
import RhwpEditorViewer from '../../components/chat/RhwpEditorViewer';
import './AssessmentPlanHwpResult.css';


const statusCopy = (status, resultOrigin) => {
  if (status === 'saving') return '수정 내용을 저장하고 있어요';
  if (status === 'checking') return '한글 문서의 저장 상태를 확인하고 있어요';
  if (status === 'saved') return '수정 내용을 임시 저장했어요';
  if (status === 'potential-edit') return '직접 수정했다면 작업 저장을 눌러주세요';
  if (status === 'save-error') return '작업 저장을 하지 못했어요. 직접 고친 내용은 아직 이 화면에만 있어요';
  if (status === 'sync-error') return 'AI 수정 내용을 한글 문서에 반영하지 못했어요';
  if (status === 'error') return '한글 문서를 열지 못했어요';
  return resultOrigin === 'generated'
    ? '새 한글 문서를 만들었어요'
    : '원본 양식에 AI 결과를 반영했어요';
};


export default function AssessmentPlanHwpResult({
  hwpResult,
  editorRef,
  editorStatus = 'ready',
  onDownload,
  onEditorError,
  onEditorReady,
  onPotentialEdit,
  onServerSaveStart,
  onServerSave,
  onServerSaveFailed,
}) {
  if (!hwpResult?.file_id) return null;

  const changeLedger = Array.isArray(hwpResult.change_ledger)
    ? hwpResult.change_ledger.filter(
        (entry) => entry && entry.status === 'applied'
      ).slice(0, 240)
    : [];

  return (
    <section
      className="assessment-rhwp-result"
      aria-busy={editorStatus === 'saving'}
      aria-label="한글 평가계획서 결과"
    >
      <div className="assessment-rhwp-result-summary">
        <div className="assessment-rhwp-result-summary-main">
          <span
            className={`assessment-rhwp-result-status assessment-rhwp-result-status--${editorStatus}`}
            aria-hidden="true"
          />
          <div>
            <p className="assessment-rhwp-result-status-text" role="status" aria-live="polite">
              {statusCopy(editorStatus, hwpResult.result_origin)}
            </p>
            <p className="assessment-rhwp-result-file">
              {hwpResult.filename || '평가계획서 한글 파일'}
            </p>
          </div>
        </div>
      </div>

      {changeLedger.length > 0 && (
        <details className="assessment-rhwp-change-ledger">
          <summary>
            <span>
              반영 내역 <strong>{changeLedger.length}개</strong>
            </span>
            <span className="assessment-rhwp-change-ledger-hint">
              어디를 왜 바꿨는지 보기
            </span>
          </summary>
          <div className="assessment-rhwp-change-ledger-list">
            {changeLedger.map((entry) => (
              <article
                key={entry.id}
                className="assessment-rhwp-change-ledger-item"
              >
                <h3>{entry.location_label || '문서 내용'}</h3>
                {(entry.before_preview || entry.after_preview) && (
                  <div className="assessment-rhwp-change-ledger-diff">
                    {entry.before_preview && (
                      <span className="assessment-rhwp-change-ledger-before">
                        {entry.before_preview}
                      </span>
                    )}
                    {entry.before_preview && entry.after_preview && (
                      <span aria-hidden="true">→</span>
                    )}
                    {entry.after_preview && (
                      <span className="assessment-rhwp-change-ledger-after">
                        {entry.after_preview}
                      </span>
                    )}
                  </div>
                )}
                {entry.reason && (
                  <p className="assessment-rhwp-change-ledger-reason">
                    <span>이유</span>
                    {entry.reason}
                  </p>
                )}
                {Array.isArray(entry.evidence_refs)
                  && entry.evidence_refs.length > 0 && (
                  <div
                    className="assessment-rhwp-change-ledger-evidence"
                    aria-label="변경 근거"
                  >
                    {entry.evidence_refs.map((evidence, evidenceIndex) => (
                      evidence.type === 'official_source'
                        && /^https?:\/\//.test(evidence.ref || '') ? (
                        <a
                          key={`${entry.id}-evidence-${evidenceIndex}`}
                          href={evidence.ref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {evidence.label}
                        </a>
                      ) : (
                        <span
                          key={`${entry.id}-evidence-${evidenceIndex}`}
                        >
                          {evidence.label}
                        </span>
                      )
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </details>
      )}

      <div className="assessment-rhwp-editor-shell">
        <RhwpEditorViewer
          ref={editorRef}
          allowPdfExport
          conversationId={`assessment-plan-${hwpResult.source_file_id || hwpResult.file_id}`}
          fileId={hwpResult.file_id}
          filename={hwpResult.filename || `평가계획서.${hwpResult.preview_kind === 'hwp_v5' ? 'hwp' : 'hwpx'}`}
          localEditNoticeVariant="assessment"
          onDownload={onDownload}
          onError={onEditorError}
          onPotentialEdit={onPotentialEdit}
          onReady={onEditorReady}
          onServerSaveStart={onServerSaveStart}
          onServerSave={onServerSave}
          onServerSaveFailed={onServerSaveFailed}
          previewKind={hwpResult.preview_kind}
          rawBytesPath={hwpResult.raw_bytes_path}
          serverSaveLabel="작업 저장"
          serverSavePath={hwpResult.server_save_path}
          telemetryPath={hwpResult.telemetry_path || false}
        />
      </div>

      <p className="assessment-rhwp-result-help">
        직접 고친 뒤 <strong>작업 저장</strong>으로 임시 보관하고, 다음에 다시 열려면 아래
        <strong> 왓퀴즈 기록에 저장</strong>도 눌러주세요.
      </p>
    </section>
  );
}
